import { useCallback, useEffect, useMemo, useState } from 'react';

type UseMaxDistanceFilterOptions = {
    readonly initialMaxDistanceMiles?: number | null;
};

type UseMaxDistanceFilterResult = {
    readonly maxDistanceMilesInput: string;
    readonly maxDistanceMiles: number | null;
    readonly handleMaxDistanceMilesInputChange: (value: string) => void;
    readonly setMaxDistanceMiles: (value: number | null) => void;
};

function formatMaxDistanceMiles(value: number | null | undefined): string {
    if (value === null || value === undefined) {
        return '';
    }

    return value.toString();
}

export function useMaxDistanceFilter(
    options?: UseMaxDistanceFilterOptions,
): UseMaxDistanceFilterResult {
    const initialMaxDistanceMiles = options?.initialMaxDistanceMiles ?? null;
    const [maxDistanceMilesInput, setMaxDistanceMilesInput] = useState(() =>
        formatMaxDistanceMiles(initialMaxDistanceMiles),
    );

    const maxDistanceMiles = useMemo(() => {
        const trimmedValue = maxDistanceMilesInput.trim();
        if (trimmedValue === '') {
            return null;
        }

        const parsedValue = Number(trimmedValue);
        if (!Number.isFinite(parsedValue) || parsedValue < 0) {
            return null;
        }

        return parsedValue;
    }, [maxDistanceMilesInput]);

    useEffect(() => {
        setMaxDistanceMilesInput(
            formatMaxDistanceMiles(initialMaxDistanceMiles),
        );
    }, [initialMaxDistanceMiles]);

    const handleMaxDistanceMilesInputChange = useCallback((value: string) => {
        if (value === '') {
            setMaxDistanceMilesInput('');
            return;
        }

        const parsedValue = Number(value);
        if (Number.isFinite(parsedValue) && parsedValue >= 0) {
            setMaxDistanceMilesInput(value);
        }
    }, []);

    const setMaxDistanceMiles = useCallback((value: number | null) => {
        setMaxDistanceMilesInput(formatMaxDistanceMiles(value));
    }, []);

    return {
        maxDistanceMilesInput,
        maxDistanceMiles,
        handleMaxDistanceMilesInputChange,
        setMaxDistanceMiles,
    };
}
